import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { dummyData, exchangeRates, SCALE_CTA } from 'src/const';
import {
  COLOR_PRIMARY_1_800,
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_2_300,
  KPI_CARD_COLOR,
} from 'src/colors';
import { User } from 'src/interfaces/iuser';
import { callApi } from 'src/core/callApi';
import { abbreviateNumber, convertCurrency, formatCurrency } from 'src/utils/functions';
import { ConfigSelect, DropdownSelect } from 'src/App/Components/Inputs';

const HistoricalPerformanceGraph = ({ listingID, handleOnSampleClick, currency }) => {
  const user: User = useSelector((state: any) => state.dashboard.user);
  const isUserAllowed = user.subscriptionPlan == 'scale';
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [selectedMainKpi, setSelectedMainKpi] = useState<string>('occupancy_rate');

  const transformData = inputData => {
    // Helper function to get the month name from a date string
    const getMonthName = dateString => {
      const date = new Date(dateString + '-01');
      return date.toLocaleString('default', { month: 'short' });
    };

    // Get the last 12 months from the input data keys
    const dataKeys = Object.keys(inputData);
    const last12Months = dataKeys.slice(-12);

    // Initialize an empty array for the transformed data
    const transformedData = last12Months.map((key, index) => {
      const currentData = inputData[key];
      const latestYear = parseInt(key.split('-')[0]);
      const previousYear = latestYear - 1;
      const twoYearsBefore = latestYear - 2;

      return {
        month: getMonthName(key), // e.g. 'Aug'
        LTMData: {
          // Latest Year
          occupancy_rate: currentData.occupancy_rate || 'unavailable',
          adr: currentData.adr || 'unavailable',
          revenue: currentData.revenue || 'unavailable',
          active_days_count: currentData.active_days_count || 'unavailable',
        },
        last2YearsData: {
          // Data from 2 years ago
          occupancy_rate: inputData[dataKeys[index + 12]]?.occupancy_rate || 'unavailable',
          adr: inputData[dataKeys[index + 12]]?.adr || 'unavailable',
          revenue: inputData[dataKeys[index + 12]]?.revenue || 'unavailable',
          active_days_count: inputData[dataKeys[index + 12]]?.active_days_count || 'unavailable',
        },
        last3YearsData: {
          // Data from 3 years ago
          occupancy_rate: inputData[dataKeys[index + 24]]?.occupancy_rate || 'unavailable',
          adr: inputData[dataKeys[index + 24]]?.adr || 'unavailable',
          revenue: inputData[dataKeys[index + 24]]?.revenue || 'unavailable',
          active_days_count: inputData[dataKeys[index + 24]]?.active_days_count || 'unavailable',
        },
        latestYear: latestYear,
        previousYear: previousYear,
        twoYearsBefore: twoYearsBefore,
        monthYear: `${getMonthName(key)},${key.split('-')[0].slice(-2)}`, // e.g. 'Aug,23'
      };
    });

    return transformedData;
  };

  const getListingHistoricalData = async listingID => {
    setLoading(true);
    const URL = 'https://8q15spida4.execute-api.us-east-2.amazonaws.com/default/getListingDetails';
    const config = {
      queryParams: {
        listingID: listingID,
        requestType: 'history',
      },
    };
    const resp = await callApi(URL, config, true);
    let listingHistoricalData = resp?.message;
    if(listingHistoricalData){
      const transformedData = transformData(listingHistoricalData);
      console.log(transformedData);
      setData(transformedData);
    }
    setLoading(false);
  };

  const renderGraph = () => (
    <ResponsiveContainer width="100%" height={250}>
      <LineChart data={data} margin={{ top: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="month"
          style={{ fontFamily: 'Roboto', fontSize: '12px' }}
          tickLine={false}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          style={{ fontFamily: 'Roboto', fontSize: '12px' }}
          tickFormatter={combineFormatterYAxis}
          domain={selectedMainKpi.includes('occupancy') && [0, 100]}
        />
        <Tooltip content={<CustomTooltip active payload label />} />
        <Legend content={<CustomLegend />} />
        <Line
          type="monotone"
          dataKey={`LTMData.${selectedMainKpi}`}
          stroke={COLOR_PRIMARY_2}
          strokeWidth={3}
        />
        <Line
          type="monotone"
          dataKey={`last2YearsData.${selectedMainKpi}`}
          stroke={COLOR_PRIMARY_1_800}
          strokeDasharray="5 5"
        />
        <Line
          type="monotone"
          dataKey={`last3YearsData.${selectedMainKpi}`}
          stroke={COLOR_PRIMARY_2_300}
          strokeDasharray="5 5"
        />
      </LineChart>
    </ResponsiveContainer>
  );

  const LockedSampleGraph = (
    <Grid
      container
      item
      xs={12}
      sx={{
        position: 'relative',
        padding: '10px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '300px',
      }}
    >
      {/* Blurred overlay to simulate a locked state */}
      <div
        style={{
          height: '100%',
          position: 'absolute',
          top: '0',
          left: '0',
          width: '100%',
          background: '#ffffff',
          filter: 'blur(6px)',
          opacity: 0.8,
          zIndex: 1, // Ensure the blur effect is on top
        }}
      >
        <Box
          sx={{
            height: '250px',
            width: '100%',
            marginLeft: '-50px',
            marginTop: '10px',
          }}
        >
          <LineChart width={500} height={250} data={dummyData}>
            <XAxis dataKey="name" tick={false} />
            <YAxis tick={false} />
            <Line type="monotone" dataKey="pv" stroke="#000" strokeWidth={3} />
          </LineChart>
        </Box>
      </div>

      {/* Centered Clickable CTA */}
      {loading ? (
        <CircularProgress />
      ) : (
        <Typography
          style={{
            fontSize: '20px',
            fontWeight: 500,
            color: COLOR_PRIMARY_2,
            cursor: 'pointer',
            pointerEvents: 'auto',
            textDecoration: 'underline',
            zIndex: 2, // Ensure the clickable overlay is above the blur
          }}
          onClick={() => {
            if (isUserAllowed) {
              getListingHistoricalData(listingID);
            } else {
              handleOnSampleClick('scale');
            }
          }}
        >
          {isUserAllowed ? 'Reveal' : SCALE_CTA}
        </Typography>
      )}
    </Grid>
  );

  const combineFormatterYAxis = (val: number) => {
    if (selectedMainKpi.includes('revenue') || selectedMainKpi.includes('adr')) {
      if (val > 1000) {
        return (
          `${currency && exchangeRates[currency].symbol}` +
          abbreviateNumber(convertCurrency(currency, val))
        );
      } else {
        return (
          `${currency && exchangeRates[currency].symbol}` +
          abbreviateNumber(convertCurrency(currency, val), false)
        );
      }
    } else if (selectedMainKpi.includes('occupancy')) {
      return val + '%';
    } else {
      return abbreviateNumber(val, false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      console.log(payload);
      let kpiName = '';

      if (selectedMainKpi == 'revenue') {
        kpiName = 'Revenue';
      } else if (selectedMainKpi == 'occupancy_rate') {
        kpiName = 'Occupancy Rate';
      } else if (selectedMainKpi == 'adr') {
        kpiName = 'Nightly Rate';
      } else {
        kpiName = 'Active Days Count';
      }

      const formatValue = value => {
        if (value === 'inactive' || value === 'unavailable') {
          return value;
        }

        return kpiName != 'Occupancy Rate' && kpiName != 'Active Days Count'
          ? `${currency && exchangeRates[currency].symbol}${formatCurrency(
              convertCurrency(currency, value).toString()
            )}`
          : `${formatCurrency(value.toString())}${kpiName == 'Occupancy Rate' ? '%' : ''}`;
      };

      return (
        <Box
          sx={{
            height: '120px',
            width: '150px',
            backgroundColor: '#ffff',
            borderRadius: '10px',
            boxShadow: '0 4px 10px #0003',
            display: 'flex',
            flexDirection: 'column',
            padding: '10px',
          }}
        >
          <Typography variant="body1" style={{ fontWeight: 'bold', color: KPI_CARD_COLOR }}>
            {kpiName}
          </Typography>
          <Typography variant="body1" style={{ fontWeight: 'bold', color: KPI_CARD_COLOR }}>
            {payload[0]['payload'].month}
          </Typography>

          {payload[0] && (
            <Typography variant="body1">
              <span>{payload[0]['payload']['latestYear']}</span> :{' '}
              <span style={{ color: COLOR_PRIMARY_2 }}>{formatValue(payload[0].value)}</span>
            </Typography>
          )}

          {payload[1] && (
            <Typography variant="body1">
              <span>{payload[1]['payload']['previousYear']}</span> :{' '}
              <span style={{ color: COLOR_PRIMARY_1_800 }}>{formatValue(payload[1].value)}</span>
            </Typography>
          )}

          {payload[2] && (
            <Typography variant="body1">
              <span>{payload[2]['payload']['twoYearsBefore']}</span> :{' '}
              <span style={{ color: COLOR_PRIMARY_2_300 }}>{formatValue(payload[2].value)}</span>
            </Typography>
          )}
        </Box>
      );
    }

    return null;
  };

  const CustomLegend = props => {
    const { payload } = props;
    return (
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1, marginRight: '10px' }}>
          <Box
            sx={{ borderTop: '4px solid #037A90', width: '15px', position: 'relative', top: '5px' }}
          />
          <Typography sx={{ fontSize: '10px' }}>Last 12 Months </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, marginRight: '10px' }}>
          <Box
            sx={{
              borderTop: '2px dashed #4FA2B1',
              width: '15px',
              position: 'relative',
              top: '5px',
            }}
          />
          <Typography sx={{ fontSize: '10px' }}>Preceding Period</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, marginRight: '10px' }}>
          <Box
            sx={{
              borderTop: `4px solid ${COLOR_PRIMARY_1_800}`,
              width: '15px',
              position: 'relative',
              top: '5px',
            }}
          />
          <Typography sx={{ fontSize: '10px' }}>Previous Year</Typography>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
        How has this listing performed historically?
      </Typography>
      <Typography variant="body1" className="subHeadings" sx={{ mb: 2 }}>
        <ConfigSelect
          value={selectedMainKpi}
          setValue={setSelectedMainKpi}
          optionsMap={{
            occupancy_rate: 'Occupancy Rate',
            adr: 'Nightly Rate',
            revenue: 'Revenue',
            active_days_count: 'Active Days Count',
          }}
          activeColor={COLOR_PRIMARY_2}
        />
        over the Last 3 years
      </Typography>
      {data ? renderGraph() : LockedSampleGraph}
    </>
  );
};

export default HistoricalPerformanceGraph;
